// ── KeyChannel — Per-key resource manager with dedicated throughput ──
//
// Each API key becomes a self-contained "channel" with its own:
//   - WebSocket connection (fill + order subscriptions)
//   - FillPoller instance (staggered across channels)
//   - Rate budget guard (rolling 1s window, per-purpose counters)
//   - Quarantine state (independent per channel)
//
// 1 key = 1 channel. 20 keys = 20 channels. Same code path.

import { KalshiNotifyWs, type NotifyCallbacks } from './KalshiNotifyWs';
import { FillPoller } from '../lib/kalshi/fillPoller';
import type { ParsedKey } from './KalshiClient';
import type { KalshiFill, KeyHealth } from '../types/kalshi';
import type { KalshiEnvironment } from '../lib/kalshi/auth';

// ── Types ───────────────────────────────────────────────────────

export type RequestPurpose = 'trading' | 'polling' | 'general';

export interface ChannelBudget {
  trading: number;   // req/s reserved for trading (default 5)
  polling: number;   // req/s reserved for polling (default 3)
  total: number;     // Kalshi hard limit (20)
}

const DEFAULT_BUDGET: ChannelBudget = {
  trading: 5,
  polling: 3,
  total: 20,
};

// ── Quarantine constants ────────────────────────────────────────

const QUARANTINE_BASE_MS = 60_000;       // 1 minute
const QUARANTINE_MAX_MS = 3_600_000;     // 1 hour
const AUTH_FAILURE_THRESHOLD = 3;        // consecutive 401/403 to trigger
const AUTH_FAILURE_WINDOW_MS = 60_000;   // window for consecutive failures

// ── Rolling window helper ───────────────────────────────────────

function pruneWindow(timestamps: number[], now: number, windowMs: number): void {
  const cutoff = now - windowMs;
  // Remove from front (oldest first)
  while (timestamps.length > 0 && timestamps[0] <= cutoff) {
    timestamps.shift();
  }
}

function countInWindow(timestamps: number[], now: number, windowMs: number): number {
  const cutoff = now - windowMs;
  let count = 0;
  for (let i = timestamps.length - 1; i >= 0; i--) {
    if (timestamps[i] > cutoff) count++;
    else break;
  }
  return count;
}

// ── KeyChannel ──────────────────────────────────────────────────

export class KeyChannel {
  readonly id: string;         // internal UUID for UI matching (falls back to accessKey)
  readonly accessKey: string;
  readonly cryptoKey: CryptoKey;
  readonly label: string;

  private ws: KalshiNotifyWs | null = null;
  private poller: FillPoller | null = null;
  private budget: ChannelBudget;

  // Rolling 1s window of request timestamps per purpose
  private tradingTs: number[] = [];
  private pollingTs: number[] = [];
  private generalTs: number[] = [];

  // Quarantine state
  private _quarantinedUntil: number | null = null;
  private quarantineCount = 0;
  private consecutiveAuthFailures = 0;
  private lastAuthFailureAt: number | null = null;

  // Health tracking
  private totalRequests = 0;
  private totalErrors = 0;
  private _lastError: string | null = null;

  constructor(parsedKey: ParsedKey, budget?: Partial<ChannelBudget>) {
    this.id = parsedKey.id ?? parsedKey.accessKey;
    this.accessKey = parsedKey.accessKey;
    this.cryptoKey = parsedKey.cryptoKey;
    this.label = parsedKey.label;
    this.budget = { ...DEFAULT_BUDGET, ...budget };
  }

  // ── Budget Guard ────────────────────────────────────────────

  private getTimestamps(purpose: RequestPurpose): number[] {
    switch (purpose) {
      case 'trading': return this.tradingTs;
      case 'polling': return this.pollingTs;
      case 'general': return this.generalTs;
    }
  }

  private getLimit(purpose: RequestPurpose): number {
    switch (purpose) {
      case 'trading': return this.budget.trading;
      case 'polling': return this.budget.polling;
      case 'general': return this.budget.total - this.budget.trading - this.budget.polling;
    }
  }

  /** Check if this channel can accept a request for the given purpose. */
  canAccept(purpose: RequestPurpose): boolean {
    if (this.isQuarantined()) return false;
    const now = Date.now();
    const ts = this.getTimestamps(purpose);
    const used = countInWindow(ts, now, 1000);
    return used < this.getLimit(purpose);
  }

  /** Record a request against this channel's budget. */
  recordRequest(purpose: RequestPurpose): void {
    const now = Date.now();
    const ts = this.getTimestamps(purpose);
    ts.push(now);
    pruneWindow(ts, now, 1000);
    this.totalRequests++;
  }

  /** Get current usage for a purpose (requests in last 1s). */
  getUsage(purpose: RequestPurpose): number {
    return countInWindow(this.getTimestamps(purpose), Date.now(), 1000);
  }

  /** Get total requests across all purposes in last 1s. */
  getTotalRate(): number {
    const now = Date.now();
    return (
      countInWindow(this.tradingTs, now, 1000) +
      countInWindow(this.pollingTs, now, 1000) +
      countInWindow(this.generalTs, now, 1000)
    );
  }

  // ── Quarantine ──────────────────────────────────────────────

  isQuarantined(): boolean {
    if (this._quarantinedUntil === null) return false;
    if (Date.now() >= this._quarantinedUntil) {
      // Auto-release
      this._quarantinedUntil = null;
      return false;
    }
    return true;
  }

  get quarantinedUntil(): number | null {
    return this.isQuarantined() ? this._quarantinedUntil : null;
  }

  quarantine(): void {
    this.quarantineCount++;
    const duration = Math.min(
      QUARANTINE_BASE_MS * 2 ** (this.quarantineCount - 1),
      QUARANTINE_MAX_MS,
    );
    this._quarantinedUntil = Date.now() + duration;
    console.warn(
      `[KeyChannel] key ${this.accessKey.slice(0, 6)}... quarantined for ${Math.round(duration / 1000)}s`,
    );
    // Pause poller while quarantined
    this.poller?.stop();
  }

  release(): void {
    this._quarantinedUntil = null;
    this.consecutiveAuthFailures = 0;
    this.quarantineCount = 0;
  }

  reportSuccess(): void {
    this.consecutiveAuthFailures = 0;
    this.lastAuthFailureAt = null;
  }

  reportError(statusCode?: number): void {
    this.totalErrors++;

    if (statusCode === 401 || statusCode === 403) {
      const now = Date.now();
      // Reset counter if outside failure window
      if (this.lastAuthFailureAt && now - this.lastAuthFailureAt > AUTH_FAILURE_WINDOW_MS) {
        this.consecutiveAuthFailures = 0;
      }
      this.consecutiveAuthFailures++;
      this.lastAuthFailureAt = now;
      this._lastError = `Auth error (${statusCode})`;

      if (this.consecutiveAuthFailures >= AUTH_FAILURE_THRESHOLD) {
        this.quarantine();
      }
    } else {
      this._lastError = statusCode ? `HTTP ${statusCode}` : 'Network error';
    }
  }

  // ── WebSocket Lifecycle ─────────────────────────────────────

  startWs(environment: KalshiEnvironment, callbacks: NotifyCallbacks): void {
    this.ws?.stop();
    this.ws = new KalshiNotifyWs(this.accessKey, this.cryptoKey, environment, callbacks);
    this.ws.start();
  }

  get wsConnected(): boolean {
    return this.ws?.connected ?? false;
  }

  // ── Poller Lifecycle ────────────────────────────────────────

  startPoller(
    fetchFills: (since?: string) => Promise<KalshiFill[]>,
    onNewFills: (fills: KalshiFill[]) => void,
    intervalMs = 500,
  ): void {
    this.poller?.stop();
    this.poller = new FillPoller(fetchFills, onNewFills);
    this.poller.start(intervalMs);
  }

  get pollerActive(): boolean {
    return this.poller !== null;
  }

  // ── Teardown ────────────────────────────────────────────────

  stopAll(): void {
    this.ws?.stop();
    this.ws = null;
    this.poller?.stop();
    this.poller = null;
  }

  // ── Health Snapshot ─────────────────────────────────────────

  getHealth(): KeyHealth {
    const now = Date.now();
    return {
      keyId: this.id,
      label: this.label,
      accessKey: truncateKey(this.accessKey),
      wsConnected: this.wsConnected,
      pollerActive: this.pollerActive,
      quarantinedUntil: this.quarantinedUntil,
      budgetUsage: {
        trading: { used: countInWindow(this.tradingTs, now, 1000), limit: this.budget.trading },
        polling: { used: countInWindow(this.pollingTs, now, 1000), limit: this.budget.polling },
        general: {
          used: countInWindow(this.generalTs, now, 1000),
          limit: this.budget.total - this.budget.trading - this.budget.polling,
        },
      },
      lastError: this._lastError,
    };
  }
}

// ── Helpers ─────────────────────────────────────────────────────

function truncateKey(accessKey: string): string {
  if (accessKey.length <= 10) return accessKey;
  return `${accessKey.slice(0, 6)}...${accessKey.slice(-4)}`;
}
